import { Suspense, useEffect } from 'react'
import { ReactLazyPreload } from 'utilities/PreloadLinks'
import { Redirect, Route, Switch } from 'react-router-dom'
import { SpinnerOverlay } from 'components/SpinnerOverlay'
import { useActiveFeatureFlags } from 'contexts/SiteConfig'
import { Home } from './Home'
import { setPageTitle } from 'utilities/PageTitle'
import {
  CUSTOMERS_VIEW,
  ORG_REPORTS_VIEW,
  REPORTS_VIEW,
  RESERVATIONS_MANAGE,
  RESERVATIONS_VIEW,
  SETTINGS_MANAGE,
} from 'constants/permissions'
import { useTranslation } from 'react-i18next'

const ReportsDirectory = ReactLazyPreload(() =>
  import('./Home').then((file) => {
    return { default: file.Home }
  })
)

const GeneralLedger = ReactLazyPreload(() =>
  import('screens/GeneralLedger/GeneralLedger').then((file) => {
    return { default: file.GeneralLedger }
  })
)

const VoucherLedger = ReactLazyPreload(() =>
  import('screens/VoucherLedger/VoucherLedger').then((file) => {
    return { default: file.VoucherLedger }
  })
)

const ProjectedMembershipRevenue = ReactLazyPreload(() =>
  import('screens/Reports/reports/ProjectedMembershipRevenue/ProjectedMembershipRevenue').then(
    (file) => {
      return { default: file.ProjectedMembershipRevenue }
    }
  )
)

const ProjectedMembershipRevenueByMonth = ReactLazyPreload(() =>
  import(
    'screens/Reports/reports/ProjectedMembershipRevenueByMonth/ProjectedMembershipRevenueByMonth'
  ).then((file) => {
    return { default: file.ProjectedMembershipRevenueByMonth }
  })
)

const DepositsHeld = ReactLazyPreload(() =>
  import('screens/DepositsHeld/DepositsHeld').then((file) => {
    return { default: file.DepositsHeld }
  })
)

const TransactionsList = ReactLazyPreload(() =>
  import('screens/TransactionsList/TransactionsList').then((file) => {
    return { default: file.TransactionsList }
  })
)

const AppointmentsList = ReactLazyPreload(() =>
  import('screens/AppointmentsList/AppointmentsList').then((file) => {
    return { default: file.AppointmentList }
  })
)

const SessionList = ReactLazyPreload(() =>
  import('screens/SessionList/SessionList').then((file) => {
    return { default: file.SessionList }
  })
)

const PractitionerUtilisationV2 = ReactLazyPreload(() =>
  import('screens/Reports/reports/PractitionerUtilisationV2')
)

const PractitionerOccupancyByDateV2 = ReactLazyPreload(() =>
  import('screens/Reports/reports/PractitionerOccupancyByDateV2')
)

const RoomOccupancyV2 = ReactLazyPreload(() => import('screens/Reports/reports/RoomOccupancyV2'))

const BookableAreaOccupancy = ReactLazyPreload(() =>
  import('screens/Reports/reports/BookableAreaOccupancy')
)

const TopOfferings = ReactLazyPreload(() =>
  import('screens/Reports/reports/TopOfferings/TopOfferings').then((file) => {
    return { default: file.TopOfferings }
  })
)

const RevenueBySalesChannel = ReactLazyPreload(() =>
  import('screens/Reports/reports/RevenueBySalesChannel/RevenueBySalesChannel').then((file) => {
    return { default: file.RevenueBySalesChannel }
  })
)

const RevenueByOrderLabel = ReactLazyPreload(() =>
  import('screens/Reports/reports/RevenueByOrderLabel/RevenueByOrderLabelV2').then((file) => {
    return { default: file.RevenueByOrderLabel }
  })
)

const RevenueAllocations = ReactLazyPreload(() =>
  import('screens/Reports/reports/RevenueAllocations/RevenueAllocations').then((file) => {
    return { default: file.RevenueAllocations }
  })
)

const RevenueAllocationsV2 = ReactLazyPreload(() =>
  import('screens/Reports/reports/RevenueAllocations/RevenueAllocationsV2').then((file) => {
    return { default: file.RevenueAllocationsV2 }
  })
)

const ItemRevenues = ReactLazyPreload(() =>
  import('screens/Reports/reports/ItemRevenues/ItemRevenues')
)

const OrderItems = ReactLazyPreload(() => import('screens/Reports/reports/OrderItems/OrderItems'))

const ServiceChargeItems = ReactLazyPreload(() =>
  import('screens/Reports/reports/ServiceChargeItems/ServiceChargeItems')
)

const Tips = ReactLazyPreload(() => import('screens/Reports/reports/Tips/Tips'))

const PromoCodeUsageSummary = ReactLazyPreload(() =>
  import('screens/Reports/reports/PromoCodeUsage/PromoCodeUsageSummary').then((file) => {
    return { default: file.PromoCodeUsageSummary }
  })
)

const PromoCodeUsageBreakdown = ReactLazyPreload(() =>
  import('screens/Reports/reports/PromoCodeUsage/PromoCodeUsageBreakdown').then((file) => {
    return { default: file.PromoCodeUsageBreakdown }
  })
)

const DiscountTypeUsageSummary = ReactLazyPreload(() =>
  import('screens/Reports/reports/DiscountTypeUsage/DiscountTypeUsageSummary').then((file) => {
    return { default: file.DiscountTypeUsageSummary }
  })
)

const DiscountTypeUsageBreakdown = ReactLazyPreload(() =>
  import('screens/Reports/reports/DiscountTypeUsage/DiscountTypeUsageBreakdown').then((file) => {
    return { default: file.DiscountTypeUsageBreakdown }
  })
)

const CancellationsAndNoShows = ReactLazyPreload(() =>
  import('screens/Reports/reports/CancellationsAndNoShows').then((file) => {
    return { default: file.CancellationsAndNoShows }
  })
)

const TopCustomers = ReactLazyPreload(() =>
  import('screens/Reports/reports/TopCustomers/TopCustomers').then((file) => {
    return { default: file.TopCustomers }
  })
)

const CheckIns = ReactLazyPreload(() => import('screens/Reports/reports/CheckIns'))

const WearablesAndPasses = ReactLazyPreload(() =>
  import('screens/Reports/reports/WearablesAndPasses')
)

const GantnerAudit = ReactLazyPreload(() => import('screens/Reports/reports/GantnerAudit'))

const LedgerLines = ReactLazyPreload(() =>
  import('screens/Reports/reports/LedgerLines/LedgerLines').then((file) => {
    return { default: file.LedgerLines }
  })
)

const TrybePaymentsBalance = ReactLazyPreload(() =>
  import('screens/Reports/reports/TrybePaymentsBalance')
)

const Payouts = ReactLazyPreload(() => import('screens/Reports/reports/Payouts'))

const Chargebacks = ReactLazyPreload(() => import('screens/Reports/reports/Chargebacks'))

const PayoutDetails = ReactLazyPreload(() =>
  import('screens/Reports/reports/Payouts/PayoutDetails')
)

const StockOnHand = ReactLazyPreload(() =>
  import('screens/Reports/reports/StockOnHand').then((file) => {
    return { default: file.StockOnHand }
  })
)

const StockAdjustments = ReactLazyPreload(() =>
  import('screens/Reports/reports/StockAdjustments').then((file) => {
    return { default: file.StockAdjustments }
  })
)

const MembershipRateBreakdown = ReactLazyPreload(() =>
  import('screens/Reports/reports/MembershipRateBreakdown')
)

const MembershipCharges = ReactLazyPreload(() =>
  import('screens/Reports/reports/MembershipCharges').then((file) => {
    return { default: file.MembershipCharges }
  })
)

const MembershipCredits = ReactLazyPreload(() =>
  import('screens/Reports/reports/MembershipCredits').then((file) => {
    return { default: file.MembershipCredits }
  })
)

const LedgerLinePostings = ReactLazyPreload(() =>
  import('screens/Reports/reports/LedgerLinePostings').then((file) => {
    return { default: file.LedgerLinePostings }
  })
)

const FiscalReceipts = ReactLazyPreload(() =>
  import('screens/Reports/reports/FiscalReceipts').then((file) => {
    return { default: file.FiscalReceipts }
  })
)

const FiscalisationActions = ReactLazyPreload(() =>
  import('screens/Reports/reports/FiscalisationActions').then((file) => {
    return { default: file.FiscalisationActions }
  })
)

const SmsUsage = ReactLazyPreload(() =>
  import('screens/Reports/reports/SmsUsage/SmsUsage').then((file) => {
    return { default: file.SmsUsage }
  })
)

const IpAddressLog = ReactLazyPreload(() =>
  import('screens/Reports/reports/IpAddressLog/IpAddressLog').then((file) => {
    return { default: file.IpAddressLog }
  })
)

const OrganisationRevenueBySalesChannel = ReactLazyPreload(() =>
  import(
    'screens/Reports/reports/organisation/OrganisationRevenueBySalesChannel/OrganisationRevenueBySalesChannel'
  ).then((file) => {
    return { default: file.OrganisationRevenueBySalesChannel }
  })
)

const OrganisationRevenueByCentre = ReactLazyPreload(() =>
  import(
    'screens/Reports/reports/organisation/OrganisationRevenueByCentre/OrganisationRevenueByCentre'
  ).then((file) => {
    return { default: file.OrganisationRevenueByCentre }
  })
)

const OrganisationTopOfferings = ReactLazyPreload(() =>
  import(
    'screens/Reports/reports/organisation/OrganisationTopOfferings/OrganisationTopOfferings'
  ).then((file) => {
    return { default: file.OrganisationTopOfferings }
  })
)

export const ReportsDirectoryRoute = () => {
  return [
    {
      path: '/reports',
      name: 'Reports',
      component: ReportsDirectory,
      exact: true,
      permissions: [REPORTS_VIEW],
    },
  ]
}

export const useRoutes = () => {
  const { t } = useTranslation()
  const featureFlags = useActiveFeatureFlags()

  const generalLedgerEnabled = featureFlags.includes('enable_general_ledger_report')
  const tipReportEnabled = featureFlags.includes('tip_report')
  const newRevenueAllocationsResponseEnabled = featureFlags.includes(
    'revenue_allocations_summary_rewrite'
  )

  return [
    {
      path: '/reports/revenue-by-sales-channel',
      name: t('reports.sales_and_organisations.revenue_by_sales_channel.title'),
      component: RevenueBySalesChannel,
      exact: true,
      category: 'sales',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/revenue-by-order-label-v2',
      name: t('reports.sales.revenue_by_order_label.title'),
      component: RevenueByOrderLabel,
      exact: true,
      category: 'sales',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/revenue-allocations-v2',
      name: t('reports.sales_and_organisations.revenue_allocations_summary.title'),
      component: RevenueAllocationsV2,
      exact: true,
      category: 'sales',
      permissions: [REPORTS_VIEW],
      hide: newRevenueAllocationsResponseEnabled,
    },
    {
      path: '/reports/revenue-allocations',
      name: t('reports.sales_and_organisations.revenue_allocations_summary.title'),
      component: RevenueAllocations,
      exact: true,
      category: 'sales',
      permissions: [REPORTS_VIEW],
      hide: !newRevenueAllocationsResponseEnabled,
    },
    {
      path: '/reports/item-revenues',
      name: t('reports.sales.revenue_allocations_breakdown.title'),
      component: ItemRevenues,
      exact: true,
      category: 'sales',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/top-offerings',
      name: t('reports.sales_and_organisations.item_sales_summary.title'),
      component: TopOfferings,
      exact: true,
      category: 'sales',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/order-items',
      name: t('reports.sales.sold_items.title'),
      component: OrderItems,
      exact: true,
      category: 'sales',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/service-charge-items',
      name: t('reports.sales.service_charge_items.title'),
      component: ServiceChargeItems,
      exact: true,
      category: 'sales',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/tips',
      name: t('reports.sales.tips.title'),
      component: Tips,
      exact: true,
      category: 'sales',
      hide: !tipReportEnabled,
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/promo-code-usage-summary',
      name: t('reports.sales.promo_code_usage_summary.title'),
      component: PromoCodeUsageSummary,
      exact: true,
      category: 'sales',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/promo-code-usage-breakdown',
      name: t('reports.sales.promo_code_usage_breakdown.title'),
      component: PromoCodeUsageBreakdown,
      exact: true,
      category: 'sales',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/discount-usage-summary',
      name: t('reports.sales.discount_usage_summary.title'),
      component: DiscountTypeUsageSummary,
      exact: true,
      category: 'sales',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/discount-usage-breakdown',
      name: t('reports.sales.discount_usage_breakdown.title'),
      component: DiscountTypeUsageBreakdown,
      exact: true,
      category: 'sales',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/no-shows-and-cancellations',
      name: t('reports.sales.cancellations_and_no_shows.title'),
      component: CancellationsAndNoShows,
      exact: true,
      category: 'sales',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/appointments',
      name: t('reports.sales.appointments.title'),
      component: AppointmentsList,
      exact: true,
      category: 'sales',
      permissions: [RESERVATIONS_VIEW],
    },
    {
      path: '/reports/sessions',
      name: t('reports.sales.sessions.title'),
      component: SessionList,
      exact: true,
      category: 'sales',
      permissions: [RESERVATIONS_VIEW],
    },
    {
      path: '/reports/top-customers',
      name: t('reports.customers.top_customers.title'),
      component: TopCustomers,
      exact: true,
      category: 'customers',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/check-ins',
      name: t('reports.customers.check_ins.title'),
      component: CheckIns,
      exact: true,
      category: 'customers',
      permissions: [CUSTOMERS_VIEW],
    },
    {
      path: '/reports/wearables-and-passes',
      name: 'Wearables and passes',
      component: WearablesAndPasses,
      exact: true,
      category: 'customers',
      permissions: [CUSTOMERS_VIEW],
    },
    {
      path: '/reports/gantner-audit',
      name: t('reports.customers.gantner_audit.title'),
      component: GantnerAudit,
      exact: true,
      category: 'customers',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/practitioner-utilisation-v2',
      name: t('reports.occupancy.practitioner_utilisation.title'),
      component: PractitionerUtilisationV2,
      exact: true,
      category: 'occupancy',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/practitioner-occupancy-by-date-v2',
      name: t('reports.occupancy.practitioner_occupancy_by_date.title'),
      component: PractitionerOccupancyByDateV2,
      exact: true,
      category: 'occupancy',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/room-occupancy-v2',
      name: t('reports.occupancy.room_occupancy.title'),
      component: RoomOccupancyV2,
      exact: true,
      category: 'occupancy',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/bookable-area-occupancy',
      name: t('reports.occupancy.bookable_area_occupancy.title'),
      component: BookableAreaOccupancy,
      exact: true,
      category: 'occupancy',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/transactions',
      name: t('reports.finance.transactions.title'),
      component: TransactionsList,
      exact: true,
      category: 'finance',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/general-ledger',
      name: 'General ledger', // doesn't seem to be used so not translating
      component: GeneralLedger,
      exact: true,
      category: 'finance',
      hide: !generalLedgerEnabled,
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/voucher-ledger',
      name: t('reports.finance.voucher_ledger.title'),
      component: VoucherLedger,
      exact: true,
      category: 'finance',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/deposits-held',
      name: t('reports.finance.deposits_held.title'),
      component: DepositsHeld,
      exact: true,
      category: 'finance',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/ledger-lines',
      name: t('reports.finance.ledger_lines.title'),
      component: LedgerLines,
      exact: true,
      category: 'finance',
    },
    {
      path: '/reports/trybe-payments-balance',
      name: 'Trybe payments balance',
      component: TrybePaymentsBalance,
      exact: true,
      // TEMP: hidden until we decide whether to remove this report and just have payouts instead
      hide: true,
      category: 'finance',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/payouts',
      name: t('reports.finance.payouts.title'),
      component: Payouts,
      exact: true,
      hide: false,
      category: 'finance',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/payouts/:payoutId',
      name: t('reports.finance.payout_details.title'),
      component: PayoutDetails,
      exact: true,
      hide: true,
      category: 'finance',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/chargebacks',
      name: t('reports.finance.chargebacks.title'),
      component: Chargebacks,
      exact: true,
      hide: false,
      category: 'finance',
      permissions: [RESERVATIONS_MANAGE],
    },
    // Operations
    {
      path: '/reports/ledger-postings',
      name: t('reports.operations.ledger_postings.title'),
      component: LedgerLinePostings,
      exact: true,
      category: 'operations',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/fiscal-receipts',
      name: t('reports.operations.fiscal_receipts.title'),
      component: FiscalReceipts,
      exact: true,
      category: 'operations',
      permissions: [REPORTS_VIEW, SETTINGS_MANAGE],
      hide: !featureFlags.includes('fiscalisation'),
    },
    {
      path: '/reports/fiscalisation-actions',
      name: t('reports.operations.fiscalisation_actions.title'),
      component: FiscalisationActions,
      exact: true,
      category: 'operations',
      permissions: [REPORTS_VIEW, SETTINGS_MANAGE],
      hide: !featureFlags.includes('fiscalisation'),
    },
    {
      path: '/reports/sms-usage',
      name: t('reports.operations.sms_usage.title'),
      component: SmsUsage,
      exact: true,
      category: 'operations',
      permissions: [REPORTS_VIEW],
      hide: !featureFlags.includes('sms_notifications'),
    },
    {
      path: '/reports/organisation/ip-address-log',
      name: t('reports.operations.login_activity.title'),
      component: IpAddressLog,
      exact: true,
      category: 'operations',
      permissions: [ORG_REPORTS_VIEW],
    },

    // Inventory
    {
      path: '/reports/stock-on-hand',
      name: t('reports.inventory.stock_on_hand.title'),
      component: StockOnHand,
      exact: true,
      category: 'inventory',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/stock-adjustments',
      name: t('reports.inventory.stock_adjustments.title'),
      component: StockAdjustments,
      exact: true,
      category: 'inventory',
      permissions: [REPORTS_VIEW],
    },

    // Memberships
    {
      path: '/reports/projected-membership-revenue',
      name: t('reports.memberships.projected_revenue_summary.title'),
      component: ProjectedMembershipRevenue,
      exact: true,
      category: 'membership',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/projected-membership-revenue-by-month',
      name: t('reports.memberships.projected_revenue_by_month.title'),
      component: ProjectedMembershipRevenueByMonth,
      exact: true,
      category: 'membership',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/membership-rate-breakdown',
      name: t('reports.memberships.membership_rate_breakdown.title'),
      component: MembershipRateBreakdown,
      exact: true,
      category: 'membership',
      permissions: [REPORTS_VIEW],
    },
    {
      path: '/reports/membership-charges',
      name: t('reports.memberships.membership_charges.title'),
      component: MembershipCharges,
      exact: true,
      category: 'membership',
    },
    {
      path: '/reports/membership-credits',
      name: 'Membership credits',
      component: MembershipCredits,
      exact: true,
      category: 'membership',
      hide: !featureFlags.includes('membership_credits_report'),
    },

    // Organisation
    {
      path: '/reports/organisation/revenue-by-centre',
      name: t('reports.sales_and_organisations.revenue_allocations_summary.title'),
      component: OrganisationRevenueByCentre,
      exact: true,
      category: 'organisation',
    },
    {
      path: '/reports/organisation/revenue-by-sales-channel',
      name: t('reports.sales_and_organisations.revenue_by_sales_channel.title'),
      component: OrganisationRevenueBySalesChannel,
      exact: true,
      category: 'organisation',
    },
    {
      path: '/reports/organisation/top-offerings',
      name: t('reports.sales_and_organisations.item_sales_summary.title'),
      component: OrganisationTopOfferings,
      exact: true,
      category: 'organisation',
    },
  ].filter(Boolean)
}

export const Reports = () => {
  const routes = useRoutes()

  return (
    <>
      <Switch>
        <Route path="/reports" exact={true} component={Home} />
        {routes.map((route, index) => (
          <Route
            key={index}
            path={route.path}
            exact={route.exact}
            component={(props) => <ReportRoute route={route} {...props} />}
          />
        ))}
        <Redirect from="*" to="/reports" />
      </Switch>
    </>
  )
}

const ReportRoute = ({ route, ...props }) => {
  useEffect(() => {
    if (route.name) {
      setPageTitle(route.name)
    }
  }, [route.name])

  return (
    <Suspense fallback={<SpinnerOverlay />}>
      <route.component {...props} />
    </Suspense>
  )
}
